import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, Select, Button, Tag, Modal, Form, Input, message, Empty, Tooltip, Popconfirm, Space, Typography, Spin, Badge } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, AppstoreOutlined, CloudOutlined, DatabaseOutlined, GlobalOutlined, ApiOutlined, HddOutlined, SafetyCertificateOutlined, ThunderboltOutlined, BlockOutlined, ReloadOutlined } from '@ant-design/icons';
import api from '../services/api';

const { Title, Text } = Typography;

const TIPO_CONFIG = {
  dns:            { label: 'DNS', icon: <GlobalOutlined />, color: '#3b82f6' },
  cdn:            { label: 'CDN / Cache', icon: <ThunderboltOutlined />, color: '#00b8d4' },
  frontend:       { label: 'Frontend', icon: <AppstoreOutlined />, color: '#042893' },
  backend:        { label: 'Backend API', icon: <ApiOutlined />, color: '#fd7e14' },
  mysql_primary:  { label: 'MySQL Primary', icon: <DatabaseOutlined />, color: '#10b759' },
  mysql_replica:  { label: 'MySQL Replica', icon: <DatabaseOutlined />, color: '#00cccc' },
  backup:         { label: 'Backup', icon: <HddOutlined />, color: '#8392a5' },
  cache:          { label: 'Cache', icon: <ThunderboltOutlined />, color: '#f10075' },
  email:          { label: 'Email', icon: <SafetyCertificateOutlined />, color: '#fd7e14' },
  storage:        { label: 'Storage', icon: <HddOutlined />, color: '#78716c' },
  other:          { label: 'Otro', icon: <BlockOutlined />, color: '#8392a5' },
};

const ESTADO_CONFIG = {
  operativo:    { color: '#10b759', bg: 'rgba(16,185,129,0.15)', label: 'Operativo', dot: '#10b759' },
  degradado:    { color: '#fd7e14', bg: 'rgba(245,158,11,0.15)', label: 'Degradado', dot: '#fd7e14' },
  caido:        { color: '#dc3545', bg: 'rgba(239,68,68,0.15)', label: 'Caído', dot: '#dc3545' },
  mantenimiento:{ color: '#3b82f6', bg: 'rgba(59,130,246,0.15)', label: 'Mantenimiento', dot: '#3b82f6' },
};

const CONN_COLORS = {
  principal: '#0168fa',
  failover: '#fd7e14',
  replica: '#10b759',
  cdn: '#00b8d4',
  proxy: '#f10075',
};

const Infraestructura = () => {
  const [proyectos, setProyectos] = useState([]);
  const [proyectoId, setProyectoId] = useState(null);
  const [componentes, setComponentes] = useState([]);
  const [conexiones, setConexiones] = useState([]);
  const [servidores, setServidores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [connModalOpen, setConnModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [connForm] = Form.useForm();

  useEffect(() => {
    api.get('/list/proyectos').then(r => {
      const list = r.data?.data || r.data?.proyectos || [];
      setProyectos(list);
      if (list.length && !proyectoId) setProyectoId(list[0].id);
    });
    api.get('/list/estadoservidores').then(r => {
      setServidores(r.data?.data || r.data?.servidores || []);
    });
  }, []); // eslint-disable-line

  const loadInfra = useCallback(async () => {
    if (!proyectoId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/list/infraestructura?proyecto_id=${proyectoId}`);
      setComponentes(data.componentes || []);
      setConexiones(data.conexiones || []);
    } catch {
      message.error('Error al cargar infraestructura');
    }
    setLoading(false);
  }, [proyectoId]);

  useEffect(() => { loadInfra(); }, [loadInfra]);

  const handleSaveComp = async (values) => {
    try {
      if (editItem) {
        await api.post(`/edit/infra-componente/${editItem.id}`, { ...values, proyecto_id: proyectoId });
      } else {
        await api.post('/add/infra-componente', { ...values, proyecto_id: proyectoId });
      }
      message.success(editItem ? 'Componente actualizado' : 'Componente agregado');
      setModalOpen(false);
      form.resetFields();
      setEditItem(null);
      loadInfra();
    } catch {
      message.error('Error al guardar componente');
    }
  };

  const handleDeleteComp = async (id) => {
    try {
      await api.get(`/delete/infra-componente/${id}`);
      message.success('Componente eliminado');
      loadInfra();
    } catch {
      message.error('Error al eliminar');
    }
  };

  const handleSaveConn = async (values) => {
    try {
      await api.post('/add/infra-conexion', { ...values, proyecto_id: proyectoId });
      message.success('Conexión agregada');
      setConnModalOpen(false);
      connForm.resetFields();
      loadInfra();
    } catch {
      message.error('Error al agregar conexión');
    }
  };

  const handleDeleteConn = async (id) => {
    try {
      await api.get(`/delete/infra-conexion/${id}`);
      message.success('Conexión eliminada');
      loadInfra();
    } catch {
      message.error('Error al eliminar conexión');
    }
  };

  const handleApplyTemplate = async (plantilla) => {
    try {
      await api.post('/infra/plantilla', { proyecto_id: proyectoId, plantilla });
      message.success('Plantilla aplicada');
      loadInfra();
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al aplicar plantilla');
    }
  };

  const openEdit = (comp) => {
    setEditItem(comp);
    form.setFieldsValue({
      tipo: comp.tipo,
      nombre: comp.nombre,
      servidor_id: comp.servidor_id || undefined,
      proveedor: comp.proveedor,
      url: comp.url,
      descripcion: comp.descripcion,
      estado: comp.estado,
      orden: comp.orden,
    });
    setModalOpen(true);
  };

  const openAdd = () => {
    setEditItem(null);
    form.resetFields();
    form.setFieldsValue({ estado: 'operativo', orden: componentes.length + 1 });
    setModalOpen(true);
  };

  // Group components by layer for diagram
  const layers = useMemo(() => {
    const order = ['dns', 'cdn', 'frontend', 'backend', 'mysql_primary', 'mysql_replica', 'cache', 'email', 'backup', 'storage', 'other'];
    const grouped = {};
    componentes.forEach(c => {
      if (!grouped[c.tipo]) grouped[c.tipo] = [];
      grouped[c.tipo].push(c);
    });
    return order.filter(t => grouped[t]).map(t => ({ tipo: t, items: grouped[t] }));
  }, [componentes]);

  const cardStyle = {
    background: '#ffffff',
    border: '1px solid #e5e9f2',
    borderRadius: 16,
    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  };

  return (
    <div className="page-container fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ color: '#001737', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <CloudOutlined style={{ color: '#0168fa' }} /> Infraestructura
          </Title>
          <Text style={{ color: '#596882' }}>Arquitectura visual por proyecto</Text>
        </div>
        <Space wrap>
          <Select
            value={proyectoId}
            onChange={setProyectoId}
            style={{ width: 220 }}
            placeholder="Seleccionar proyecto"
            options={proyectos.map(p => ({ value: p.id, label: p.nombre }))}
          />
          <Button icon={<ReloadOutlined />} onClick={loadInfra}>Refrescar</Button>
        </Space>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60 }}><Spin size="large" /></div>
      ) : componentes.length === 0 ? (
        <Card style={cardStyle}>
          <Empty description={<Text style={{ color: '#596882' }}>No hay componentes de infraestructura</Text>}>
            <Space direction="vertical" align="center">
              <Space>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
                  style={{ background: 'linear-gradient(135deg, #0168fa, #042893)', border: 'none', borderRadius: 8 }}>
                  Agregar Componente
                </Button>
              </Space>
              <Text style={{ color: '#596882', fontSize: 12 }}>O aplica una plantilla predefinida:</Text>
              <Space>
                <Button onClick={() => handleApplyTemplate('estandar')} style={{ borderColor: '#0168fa', color: '#0168fa' }}>
                  Plantilla Estándar
                </Button>
                <Button onClick={() => handleApplyTemplate('alta_disponibilidad')} style={{ borderColor: '#10b759', color: '#10b759' }}>
                  Alta Disponibilidad
                </Button>
              </Space>
            </Space>
          </Empty>
        </Card>
      ) : (
        <>
          {/* Action bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
              style={{ background: 'linear-gradient(135deg, #0168fa, #042893)', border: 'none', borderRadius: 8 }}>
              Componente
            </Button>
            <Button icon={<PlusOutlined />} onClick={() => { connForm.resetFields(); setConnModalOpen(true); }}
              style={{ borderColor: '#0168fa', color: '#0168fa' }}>
              Conexión
            </Button>
          </div>

          {/* Visual Diagram */}
          <Card style={{ ...cardStyle, marginBottom: 20, overflow: 'auto' }}>
            <div style={{ minWidth: 600 }}>
              {layers.map((layer, li) => {
                const tc = TIPO_CONFIG[layer.tipo] || TIPO_CONFIG.other;
                return (
                  <div key={layer.tipo} style={{ marginBottom: li < layers.length - 1 ? 0 : 0 }}>
                    {/* Layer label */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <div style={{ width: 3, height: 16, background: tc.color, borderRadius: 2 }} />
                      <Text style={{ color: '#8392a5', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{tc.label}</Text>
                    </div>

                    {/* Components row */}
                    <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                      {layer.items.map(comp => {
                        const ec = ESTADO_CONFIG[comp.estado] || ESTADO_CONFIG.operativo;
                        return (
                          <div key={comp.id}
                            style={{
                              background: ec.bg,
                              border: `2px solid ${ec.color}40`,
                              borderRadius: 14,
                              padding: '14px 20px',
                              minWidth: 160,
                              position: 'relative',
                              transition: 'all 0.2s',
                              cursor: 'pointer',
                            }}
                            onClick={() => openEdit(comp)}
                          >
                            <Badge dot color={ec.dot} offset={[-4, 4]} style={{ position: 'absolute', top: 8, right: 8 }}>
                              <span />
                            </Badge>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                              <span style={{ color: tc.color, fontSize: 18 }}>{tc.icon}</span>
                              <Text strong style={{ color: '#001737', fontSize: 14 }}>{comp.nombre}</Text>
                            </div>
                            {comp.proveedor && (
                              <Text style={{ color: '#596882', fontSize: 11, display: 'block' }}>{comp.proveedor}</Text>
                            )}
                            {comp.servidor_nombre && (
                              <Text style={{ color: '#8392a5', fontSize: 11, display: 'block' }}>
                                🖥 {comp.servidor_nombre} ({comp.servidor_ip})
                              </Text>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                              <Tag color={ec.color} style={{ margin: 0, fontSize: 10 }}>{ec.label}</Tag>
                              <Popconfirm title="¿Eliminar componente?" onConfirm={(e) => { e?.stopPropagation(); handleDeleteComp(comp.id); }}
                                onCancel={e => e?.stopPropagation()}>
                                <Button type="text" size="small" icon={<DeleteOutlined />} danger
                                  onClick={e => e.stopPropagation()} style={{ opacity: 0.6 }} />
                              </Popconfirm>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Connection arrows between layers */}
                    {li < layers.length - 1 && (
                      <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          {conexiones
                            .filter(cn => {
                              const srcInThisLayer = layer.items.some(c => c.id === cn.origen_id);
                              const dstInNextLayer = layers[li + 1]?.items.some(c => c.id === cn.destino_id);
                              return srcInThisLayer && dstInNextLayer;
                            })
                            .slice(0, 3)
                            .map((cn, ci) => {
                              const cc = CONN_COLORS[cn.tipo] || CONN_COLORS.principal;
                              return (
                                <Tooltip key={cn.id} title={`${cn.origen_nombre} → ${cn.destino_nombre} (${cn.tipo})${cn.label ? ': ' + cn.label : ''}`}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}
                                    onClick={() => handleDeleteConn(cn.id)}>
                                    <div style={{ width: 2, height: 20, background: cc, opacity: 0.6 }} />
                                    {cn.label && <Text style={{ color: cc, fontSize: 10 }}>{cn.label}</Text>}
                                  </div>
                                </Tooltip>
                              );
                            })
                          }
                          {(() => {
                            const count = conexiones.filter(cn => {
                              const srcInThisLayer = layer.items.some(c => c.id === cn.origen_id);
                              const dstInNextLayer = layers[li + 1]?.items.some(c => c.id === cn.destino_id);
                              return srcInThisLayer && dstInNextLayer;
                            }).length;
                            return count === 0 ? (
                              <div style={{ width: 2, height: 24, background: '#e5e9f2', borderRadius: 1 }} />
                            ) : null;
                          })()}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Connections list */}
          {conexiones.length > 0 && (
            <Card style={cardStyle} title={<Text style={{ color: '#001737' }}>Conexiones ({conexiones.length})</Text>}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {conexiones.map(cn => {
                  const cc = CONN_COLORS[cn.tipo] || CONN_COLORS.principal;
                  return (
                    <Tag key={cn.id} closable onClose={() => handleDeleteConn(cn.id)}
                      style={{ background: `${cc}20`, border: `1px solid ${cc}40`, color: cc, borderRadius: 8 }}>
                      {cn.origen_nombre} → {cn.destino_nombre}
                      {cn.label && ` (${cn.label})`}
                      <Text style={{ color: '#596882', fontSize: 10, marginLeft: 4 }}>[{cn.tipo}]</Text>
                    </Tag>
                  );
                })}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Add/Edit Component Modal */}
      <Modal
        open={modalOpen}
        title={<Text style={{ color: '#001737' }}>{editItem ? 'Editar Componente' : 'Agregar Componente'}</Text>}
        onCancel={() => { setModalOpen(false); setEditItem(null); form.resetFields(); }}
        onOk={() => form.submit()}
        okText="Guardar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: '#0168fa', border: 'none' } }}
      >
        <Form form={form} layout="vertical" onFinish={handleSaveComp} requiredMark={false}>
          <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
            <Select options={Object.entries(TIPO_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))} />
          </Form.Item>
          <Form.Item name="nombre" label="Nombre" rules={[{ required: true }]}>
            <Input placeholder="Ej: MySQL Primary Bogotá" />
          </Form.Item>
          <Form.Item name="servidor_id" label="Servidor asociado">
            <Select allowClear placeholder="(Opcional) Vincular a servidor"
              options={servidores.map(s => ({ value: s.id, label: `${s.nombre} (${s.ip})` }))} />
          </Form.Item>
          <Form.Item name="proveedor" label="Proveedor">
            <Input placeholder="Ej: Cloudflare Free, cPanel OpenLiteSpeed" />
          </Form.Item>
          <Form.Item name="url" label="URL">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="descripcion" label="Descripción">
            <Input.TextArea rows={2} />
          </Form.Item>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Form.Item name="estado" label="Estado">
              <Select options={Object.entries(ESTADO_CONFIG).map(([k, v]) => ({ value: k, label: v.label }))} />
            </Form.Item>
            <Form.Item name="orden" label="Orden">
              <Input type="number" />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      {/* Add Connection Modal */}
      <Modal
        open={connModalOpen}
        title={<Text style={{ color: '#001737' }}>Agregar Conexión</Text>}
        onCancel={() => { setConnModalOpen(false); connForm.resetFields(); }}
        onOk={() => connForm.submit()}
        okText="Guardar"
        cancelText="Cancelar"
        okButtonProps={{ style: { background: '#0168fa', border: 'none' } }}
      >
        <Form form={connForm} layout="vertical" onFinish={handleSaveConn} requiredMark={false}>
          <Form.Item name="origen_id" label="Origen" rules={[{ required: true }]}>
            <Select placeholder="Componente de origen"
              options={componentes.map(c => ({ value: c.id, label: `${c.nombre} (${TIPO_CONFIG[c.tipo]?.label || c.tipo})` }))} />
          </Form.Item>
          <Form.Item name="destino_id" label="Destino" rules={[{ required: true }]}>
            <Select placeholder="Componente destino"
              options={componentes.map(c => ({ value: c.id, label: `${c.nombre} (${TIPO_CONFIG[c.tipo]?.label || c.tipo})` }))} />
          </Form.Item>
          <Form.Item name="tipo" label="Tipo de Conexión" initialValue="principal">
            <Select options={[
              { value: 'principal', label: 'Principal' },
              { value: 'failover', label: 'Failover' },
              { value: 'replica', label: 'Réplica' },
              { value: 'cdn', label: 'CDN' },
              { value: 'proxy', label: 'Proxy' },
            ]} />
          </Form.Item>
          <Form.Item name="label" label="Etiqueta">
            <Input placeholder="Ej: DB Write/Read, Replicación" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Infraestructura;
